import type { KVKSearchPayload, KVKSearchResult, QuoteListStatusResponse } from '@/types/api';
import type {
	ActivationResponse,
	AppData,
	ApplicationResponse,
	ApplicationUpdatePayload,
	CocPayload,
	ConfigData,
	ContactPayload,
	CoveragePayload,
	EmployeeImportResponse,
	FormDataFields,
	HeaderOptions,
	LabelPasswordAuthResponse,
	OccupationalHealthServiceQuote,
	PolicyUploadResponse,
	PremiumPercentagePayload,
	PremiumVarsPayload,
	ProductTypeCoveragePayload,
	QuoteListResponse,
	SectorCodePayload,
	TokenRefreshResponse,
	WageInfoPayload
} from '@/types/app';
import type { ExtractionResponse } from '@/types/employee';

/**
 * Thrown when the server responds with 429 Too Many Requests.
 */
export class RateLimitError extends Error {
	constructor(message = 'Rate limited') {
		super(message);
		this.name = 'RateLimitError';
	}
}

/**
 * Structured error from API responses (401/403 and other non-OK responses).
 * The backend returns {"detail": {"code": "...", "message": "..."}} for auth errors.
 */
export class ApiError extends Error {
	code: string | undefined;
	status: number;

	constructor(status: number, code: string | undefined, message: string) {
		super(message);
		this.name = 'ApiError';
		this.code = code;
		this.status = status;
	}
}

/**
 * Base API URL from environment configuration
 */
const API_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Current environment (development, test, stage, production)
 */
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT;

/**
 * Captcha bypass key for testing environments
 */
const CAPTCHA_BYPASS_KEY = import.meta.env.VITE_CAPTCHA_BYPASS_KEY;

/**
 * Gets captcha bypass key from cookie if available
 * This allows E2E tests to bypass captcha in production-like environments
 * @returns The captcha bypass key from cookie, or null if not found
 */
export const getCaptchaBypassFromCookie = (): string | null => {
	if (typeof document === 'undefined') return null;

	const cookies = document.cookie.split(';');
	const captchaCookie = cookies.find((cookie) => cookie.trim().startsWith('captchaBypassKey='));

	if (!captchaCookie) return null;

	const value = captchaCookie.split('=')[1]?.trim();
	return value && value !== '' ? value : null;
};

/**
 * Gets the appropriate captcha solution based on environment and cookies
 * Priority: Cookie bypass > Environment bypass > Provided solution
 * @param providedSolution - The captcha solution provided by the user/form
 * @returns The captcha solution to use
 */
const resolveCaptchaSolution = (providedSolution: string): string => {
	// First priority: Check for cookie-based bypass (for E2E tests)
	const cookieBypass = getCaptchaBypassFromCookie();
	if (cookieBypass) {
		return cookieBypass;
	}

	// Second priority: Environment-based bypass (for development/test)
	if ((ENVIRONMENT === 'test' || ENVIRONMENT === 'development') && CAPTCHA_BYPASS_KEY) {
		return CAPTCHA_BYPASS_KEY;
	}

	// Default: Use the provided solution
	return providedSolution;
};

/**
 * UUID pattern for extracting policy_upload_form IDs from URL paths
 */
const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Attempts to find a Bearer token from the current URL context.
 * Extracts a UUID from the URL path and looks up the corresponding
 * access token in localStorage.
 * @returns The access token string if found, null otherwise
 */
const getAvailableBearerToken = (): string | null => {
	const match = window.location.pathname.match(UUID_REGEX);
	if (!match) return null;
	return localStorage.getItem(match[0]);
};

/**
 * Handles expired label password authentication.
 * Only triggers for label-password-specific 401s (not JWT bearer token 401s).
 * If the stored label auth token is stale, removes it and reloads to show PasswordGate.
 * @param errorCode - The structured error code from the API response, if available
 * @param responseText - The raw response body from the 401 response (fallback matching)
 * @returns true if the label auth token was cleared and page is reloading
 */
const handleExpiredLabelAuth = (errorCode: string | undefined, responseText: string): boolean => {
	const isLabelAuthError =
		errorCode === 'label_password_expired' ||
		errorCode === 'invalid_label_password_token' ||
		responseText.includes('label password') ||
		responseText.includes('Label password');
	if (!isLabelAuthError) return false;

	const hostname = window.location.hostname;
	const labelSlug = hostname.split('.')[0];
	const authTokenKey = `quotr_label_auth_${labelSlug}`;
	const authToken = localStorage.getItem(authTokenKey);

	if (authToken) {
		localStorage.removeItem(authTokenKey);
		window.location.reload();
		return true;
	}

	return false;
};

/**
 * Reads a Server-Sent Events stream over `fetch` (so the Authorization header is
 * sent — native EventSource cannot set headers). Parses `event:`/`data:` frames and
 * invokes `onEvent` per frame; comment/keepalive lines are ignored. Resolves when the
 * server closes the stream or `signal` aborts; rejects on a non-OK response.
 *
 * @param url - Absolute SSE endpoint URL
 * @param headers - Request headers (include Authorization + Accept: text/event-stream)
 * @param signal - AbortSignal used to close the stream from the caller
 * @param onEvent - Called with (eventName, data) for each parsed frame
 */
const readSse = async (
	url: string,
	headers: Record<string, string>,
	signal: AbortSignal,
	onEvent: (event: string, data: string) => void
): Promise<void> => {
	const response = await fetch(url, { method: 'GET', headers, signal });
	if (!response.ok || !response.body) {
		throw new ApiError(response.status, undefined, `SSE request failed (${response.status})`);
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	// SSE frames are separated by a blank line; a line ends with \r\n, \r, or \n.
	// We keep the buffer raw (no per-chunk CRLF normalization) and match each
	// terminator sequence, so a \r\n split across two read() chunks can't hide a
	// frame boundary or line break.
	const FRAME_SEPARATOR = /\r\n\r\n|\n\n|\r\r/;
	const LINE_SEPARATOR = /\r\n|\n|\r/;
	let buffer = '';
	try {
		for (;;) {
			const { done, value } = await reader.read();
			if (done) break;
			buffer += decoder.decode(value, { stream: true });

			let match = FRAME_SEPARATOR.exec(buffer);
			while (match !== null) {
				const frame = buffer.slice(0, match.index);
				buffer = buffer.slice(match.index + match[0].length);

				let eventName = 'message';
				const dataLines: string[] = [];
				for (const line of frame.split(LINE_SEPARATOR)) {
					if (line.startsWith('event:')) eventName = line.slice(6).trim();
					else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
				}
				if (dataLines.length > 0 || eventName !== 'message') {
					onEvent(eventName, dataLines.join('\n'));
				}

				match = FRAME_SEPARATOR.exec(buffer);
			}
		}
	} finally {
		reader.cancel().catch(() => {});
	}
};

/**
 * A file fetched from the API, ready to be handed to the browser.
 */
export interface DownloadedFile {
	blob: Blob;
	/** The name from `Content-Disposition`, or null when the header is unreadable. */
	filename: string | null;
}

/**
 * Reads the filename out of a `Content-Disposition` header.
 *
 * Prefers the RFC 5987 `filename*` form so non-ASCII names survive, and falls
 * back to the plain quoted `filename`. Returns null when the header is absent —
 * cross-origin responses only expose it when the API lists it in
 * `Access-Control-Expose-Headers`.
 */
export const parseAttachmentFilename = (header: string | null): string | null => {
	if (!header) return null;

	// The language field between the quotes is optional (RFC 8187), and the
	// parameter is anchored to a boundary so it can't match inside another value.
	const extended = header.match(/(?:^|;)\s*filename\*\s*=\s*UTF-8'[A-Za-z0-9-]*'([^;]+)/i);
	if (extended?.[1]) {
		try {
			return decodeURIComponent(extended[1]);
		} catch {
			// Malformed percent-encoding: fall through to the plain parameter.
		}
	}

	const plain = header.match(/filename="([^"]*)"/i);
	return plain?.[1] ? plain[1] : null;
};

/**
 * Main API service object containing all API endpoints and utility functions
 */
const api = {
	/**
	 * Authentication-related API endpoints
	 */
	auth: {
		/**
		 * Authenticates with label password
		 * @param password - The password/email to authenticate with
		 * @returns Promise resolving to authentication response with token
		 */
		labelPassword: async (password: string): Promise<LabelPasswordAuthResponse> => {
			const response = await fetch(`${API_URL}/auth/label-password`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Origin: window.location.origin
				},
				body: JSON.stringify({ password })
			});
			return api.handleResponse(response) as Promise<LabelPasswordAuthResponse>;
		}
	},
	/**
	 * Configuration-related API endpoints
	 */
	config: {
		/**
		 * Posts initial href to config endpoint to get app configuration
		 * @param initialHref - The initial URL/href for configuration
		 * @returns Promise resolving to configuration data
		 */
		post: async (initialHref: string): Promise<ConfigData> => {
			const response = await fetch(`${API_URL}/config`, {
				method: 'POST',
				headers: api.formatHeaders(),
				body: JSON.stringify({ initial_href: initialHref })
			});
			return api.handleResponse(response) as Promise<ConfigData>;
		},
		/**
		 * Fetches config with label password authentication
		 * @param token - The authentication token
		 * @param initialHref - The initial URL/href for configuration
		 * @returns Promise resolving to configuration data
		 */
		fetchWithAuth: async (token: string, initialHref: string): Promise<ConfigData> => {
			const response = await fetch(`${API_URL}/config`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Label-Password-Auth': token,
					Origin: window.location.origin
				},
				body: JSON.stringify({ initial_href: initialHref })
			});
			return api.handleResponse(response) as Promise<ConfigData>;
		}
	},
	/**
	 * Policy-related API endpoints
	 */
	policy: {
		/**
		 * Uploads a policy file with captcha solution
		 * @param file - The policy file to upload
		 * @param captchaSolution - The captcha solution
		 * @param initialHref - The initial URL/href
		 * @returns Promise resolving to policy upload response
		 */
		upload: async (
			file: File,
			captchaSolution: string,
			initialHref: string
		): Promise<PolicyUploadResponse> => {
			const data: FormDataFields = {};
			data.file = file;
			data.captchaSolution = resolveCaptchaSolution(captchaSolution);
			data.initial_href = initialHref;
			const formData = api.formatFormData(data);
			const response = await fetch(`${API_URL}/policy-upload-forms`, {
				method: 'POST',
				headers: api.formatHeaders({ contentType: false }),
				body: formData
			});
			return api.handleResponse(response) as Promise<PolicyUploadResponse>;
		},
		/**
		 * Submits a policy upload form
		 * @param id - The policy upload form ID
		 * @returns Promise resolving to app data
		 */
		submit: async (id: string): Promise<AppData> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/submit`, {
				method: 'POST',
				headers: api.formatHeaders({ id })
			});
			return api.handleResponse(response) as Promise<AppData>;
		},
		/**
		 * Creates and submits a form without a file (no-policy flow).
		 * Uses the one-stop POST /policy-upload-forms/submit endpoint.
		 * @param captchaSolution - The captcha solution
		 * @param initialHref - The initial URL/href
		 * @returns Promise resolving to policy upload response
		 */
		submitWithoutFile: async (
			captchaSolution: string,
			initialHref: string
		): Promise<PolicyUploadResponse> => {
			const data: FormDataFields = {};
			data.captchaSolution = resolveCaptchaSolution(captchaSolution);
			data.initial_href = initialHref;
			const formData = api.formatFormData(data);
			const response = await fetch(`${API_URL}/policy-upload-forms/submit`, {
				method: 'POST',
				headers: api.formatHeaders({ contentType: false }),
				body: formData
			});
			return api.handleResponse(response) as Promise<PolicyUploadResponse>;
		},
		/**
		 * Uploads a current-policy PDF to an existing form (no-policy / initial-payload
		 * start) so the backend backfills current premium & coverage (issue #961).
		 * @param id - The policy upload form ID
		 * @param file - The current-policy PDF
		 * @param captchaSolution - The captcha solution
		 */
		uploadDocumentLater: async (id: string, file: File, captchaSolution: string): Promise<void> => {
			const data: FormDataFields = {
				file,
				captchaSolution: resolveCaptchaSolution(captchaSolution)
			};
			const formData = api.formatFormData(data);
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/policy-document`, {
				method: 'POST',
				headers: api.formatHeaders({ id, contentType: false }),
				body: formData
			});
			await api.handleResponse(response);
		},
		/**
		 * Downloads the current-policy document the customer uploaded.
		 * @param id - The policy upload form ID
		 * @returns The file contents plus the name the backend serves it under
		 */
		downloadDocument: async (id: string): Promise<DownloadedFile> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/policy-document`, {
				headers: api.formatHeaders({ id, contentType: false, accept: '*/*' })
			});
			// Non-OK bodies are JSON, so let the shared handler raise the ApiError.
			if (!response.ok) await api.handleResponse(response);
			return {
				blob: await response.blob(),
				filename: parseAttachmentFilename(response.headers.get('Content-Disposition'))
			};
		}
	},
	/**
	 * Quote-related API endpoints
	 */
	quote: {
		/**
		 * Sick leave short term quote endpoints
		 */
		sick_leave_short_term: {
			/**
			 * Fetches sick leave short term quotes
			 * @param id - The policy upload form ID
			 * @returns Promise resolving to app data with quotes
			 */
			fetch: async (id: string): Promise<AppData> => {
				const response = await fetch(`${API_URL}/policy-upload-forms/${id}/quotes`, {
					headers: api.formatHeaders({ id })
				});
				return api.handleResponse(response) as Promise<AppData>;
			}
		},
		/**
		 * Occupational health service quote endpoints
		 */
		occupational_health_service: {
			/**
			 * Fetches occupational health service quotes
			 * @param id - The policy upload form ID
			 * @returns Promise resolving to array of occupational health service quotes
			 */
			fetch: async (id: string): Promise<OccupationalHealthServiceQuote[]> => {
				const response = await fetch(
					`${API_URL}/policy-upload-forms/${id}/quotes/arbo-dienstverlening`,
					{
						headers: api.formatHeaders({ id })
					}
				);
				return api.handleResponse(response) as Promise<OccupationalHealthServiceQuote[]>;
			}
		},
		/**
		 * Generic per-product-type quote endpoints.
		 * PATCH triggers quote retrieval in the background; GET fetches the
		 * latest quotes (or current status while still processing).
		 */
		byProductType: {
			/**
			 * Triggers quote retrieval for a specific product type (PATCH).
			 * Returns a status-only payload — quotes are computed asynchronously
			 * and must be fetched via ``fetch`` (poll until status is no longer
			 * ``PROCESSING_QUOTES``). When coverage is unchanged and quotes
			 * already exist, the backend short-circuits without re-triggering.
			 * @param id - The policy upload form ID
			 * @param productType - The product type string (e.g. 'sick_leave_long_term_wga_gap_extended')
			 * @param coverage - Optional coverage overrides
			 */
			trigger: async (
				id: string,
				productType: string,
				coverage?: ProductTypeCoveragePayload
			): Promise<QuoteListStatusResponse> => {
				const response = await fetch(`${API_URL}/policy-upload-forms/${id}/quotes/${productType}`, {
					method: 'PATCH',
					headers: api.formatHeaders({ id }),
					body: JSON.stringify(coverage ?? {})
				});
				return api.handleResponse(response) as Promise<QuoteListStatusResponse>;
			},
			/**
			 * Fetches quotes for a specific product type (GET / poll).
			 * @param id - The policy upload form ID
			 * @param productType - The product type string
			 */
			fetch: async (id: string, productType: string): Promise<QuoteListResponse> => {
				const response = await fetch(`${API_URL}/policy-upload-forms/${id}/quotes/${productType}`, {
					method: 'GET',
					headers: api.formatHeaders({ id })
				});
				return api.handleResponse(response) as Promise<QuoteListResponse>;
			},
			/**
			 * Streams quotes for a product type via Server-Sent Events.
			 *
			 * Emits a `quotes` event (full `QuoteListResponse`) on connect and again as
			 * each quote is persisted, then a `done` event when the fetch finishes. Use
			 * while a fetch is in flight instead of polling. Resolves when the stream
			 * ends; rejects on transport error (callers fall back to `fetch` polling).
			 * @param id - The policy upload form ID
			 * @param productType - The product type string
			 * @param handlers - `onQuotes` per snapshot, `onDone` when finished
			 * @param signal - AbortSignal to close the stream (e.g. on unmount/refetch)
			 */
			stream: (
				id: string,
				productType: string,
				handlers: { onQuotes: (data: QuoteListResponse) => void; onDone: () => void },
				signal: AbortSignal
			): Promise<void> =>
				readSse(
					`${API_URL}/policy-upload-forms/${id}/quotes/${productType}/stream`,
					api.formatHeaders({ id, contentType: false, accept: 'text/event-stream' }),
					signal,
					(event, data) => {
						if (event === 'done') {
							handlers.onDone();
						} else if (event === 'quotes' && data) {
							handlers.onQuotes(JSON.parse(data) as QuoteListResponse);
						}
					}
				)
		}
	},
	/**
	 * Premium variables API endpoints
	 */
	premiumVars: {
		/**
		 * Updates premium variables for a policy upload form
		 * @param id - The policy upload form ID
		 * @param premiumVars - The premium variables payload
		 * @returns Promise resolving to updated app data
		 */
		update: async (id: string, premiumVars: PremiumVarsPayload): Promise<AppData> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/premium-variables`, {
				method: 'POST',
				headers: api.formatHeaders({ id }),
				body: JSON.stringify(premiumVars)
			});
			return api.handleResponse(response) as Promise<AppData>;
		}
	},
	/**
	 * Chamber of Commerce (CoC) API endpoints
	 */
	coc: {
		/**
		 * Search for companies by name
		 * @param id - The policy upload form ID
		 * @param searchParams - Company name and pagination parameters
		 * @returns Promise resolving to list of matching companies
		 */
		search: async (id: string, searchParams: KVKSearchPayload): Promise<KVKSearchResult> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/coc-lookup`, {
				method: 'POST',
				headers: api.formatHeaders({ id }),
				body: JSON.stringify(searchParams)
			});
			return api.handleResponse(response) as Promise<KVKSearchResult>;
		},
		/**
		 * Updates Chamber of Commerce information
		 * @param id - The policy upload form ID
		 * @param coc - The CoC payload
		 * @returns Promise resolving to updated app data
		 */
		update: async (id: string, coc: CocPayload): Promise<AppData> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/coc`, {
				method: 'PATCH',
				headers: api.formatHeaders({ id }),
				body: JSON.stringify(coc)
			});
			return api.handleResponse(response) as Promise<AppData>;
		}
	},
	/**
	 * Sector code API endpoints
	 */
	sectorCode: {
		/**
		 * Updates sector code for a policy upload form
		 * @param id - The policy upload form ID
		 * @param sectorCode - The sector code payload
		 * @returns Promise resolving to updated app data
		 */
		update: async (id: string, sectorCode: SectorCodePayload): Promise<AppData> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/sector-code`, {
				method: 'PATCH',
				headers: api.formatHeaders({ id }),
				body: JSON.stringify(sectorCode)
			});
			return api.handleResponse(response) as Promise<AppData>;
		}
	},
	/**
	 * Sector code API endpoints
	 */
	wageInfo: {
		/**
		 * Updates sector code for a policy upload form
		 * @param id - The policy upload form ID
		 * @param sectorCode - The sector code payload
		 * @returns Promise resolving to updated app data
		 */
		update: async (id: string, wageInfo: WageInfoPayload): Promise<AppData> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/wage-info`, {
				method: 'PATCH',
				headers: api.formatHeaders({ id }),
				body: JSON.stringify(wageInfo)
			});
			return api.handleResponse(response) as Promise<AppData>;
		}
	},
	/**
	 * SLST coverage API endpoints
	 */
	slstCoverage: {
		/**
		 * Submits sick-leave short-term coverage for the no-policy flow
		 * @param id - The policy upload form ID
		 * @param coverage - The coverage payload with sick_leave_short_term
		 * @returns Promise resolving to updated app data
		 */
		update: async (id: string, coverage: CoveragePayload): Promise<AppData> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/slst-coverage`, {
				method: 'PATCH',
				headers: api.formatHeaders({ id }),
				body: JSON.stringify(coverage)
			});
			return api.handleResponse(response) as Promise<AppData>;
		}
	},
	/**
	 * Premium percentage API endpoints
	 */
	premiumPercentage: {
		/**
		 * Updates current premium percentage for a policy upload form
		 * @param id - The policy upload form ID
		 * @param premiumPercentage - The premium percentage payload (null to clear)
		 * @returns Promise resolving to updated app data
		 */
		update: async (
			id: string,
			premiumPercentage: PremiumPercentagePayload
		): Promise<QuoteListStatusResponse> => {
			const response = await fetch(
				`${API_URL}/policy-upload-forms/${id}/current-premium-percentage`,
				{
					method: 'POST',
					headers: api.formatHeaders({ id }),
					body: JSON.stringify(premiumPercentage)
				}
			);
			return api.handleResponse(response) as Promise<QuoteListStatusResponse>;
		}
	},
	/**
	 * Application-related API endpoints
	 */
	application: {
		/**
		 * Gets application data for a policy upload form (pre-filled from contact form)
		 * @param id - The policy upload form ID
		 * @returns Promise resolving to application response with pre-filled data
		 */
		get: async (id: string): Promise<ApplicationResponse> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/application`, {
				method: 'GET',
				headers: api.formatHeaders({ id })
			});
			return api.handleResponse(response) as Promise<ApplicationResponse>;
		},
		/**
		 * Updates application data for a policy upload form
		 * @param id - The policy upload form ID
		 * @param formData - The application update payload
		 * @returns Promise resolving to updated app data
		 */
		update: async (id: string, formData: ApplicationUpdatePayload): Promise<AppData> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/application`, {
				method: 'PATCH',
				headers: api.formatHeaders({ id }),
				body: JSON.stringify(formData)
			});
			return api.handleResponse(response) as Promise<AppData>;
		},
		/**
		 * Submits application for a policy upload form
		 * @param id - The policy upload form ID
		 * @returns Promise resolving to app data
		 */
		submit: async (id: string): Promise<AppData> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/application/submit`, {
				method: 'POST',
				headers: api.formatHeaders({ id })
			});
			return api.handleResponse(response) as Promise<AppData>;
		},
		/**
		 * Signals the user has finished picking quotes so the backend can schedule
		 * the offer-information email. Called on every "Aanvraag indienen" click —
		 * idempotent re-sends are by design when the user re-picks and re-confirms.
		 */
		confirmQuoteSelection: async (id: string, signal?: AbortSignal): Promise<AppData> => {
			const response = await fetch(
				`${API_URL}/policy-upload-forms/${id}/application/confirm-quote-selection`,
				{
					method: 'POST',
					headers: api.formatHeaders({ id }),
					signal
				}
			);
			return api.handleResponse(response) as Promise<AppData>;
		}
	},
	/**
	 * Contact form API endpoints
	 */
	contact: {
		/**
		 * Submits contact form with captcha solution.
		 *
		 * When the caller has a policy upload form id (i.e. the customer is
		 * inside the funnel), the backend will attach proposal context
		 * (selected insurer + `/aanbod/{id}/{jwt}` advisor link) to the advisor
		 * email
		 * automatically. Public landing-page submissions just pass an empty id.
		 */
		submit: async (
			id: string,
			email: string,
			phone: string,
			name: string,
			captchaSolution: string,
			message?: string
		): Promise<void> => {
			// Backend ContactMeRequest treats name/phone/email as optional (EmailStr | None)
			// with a "phone OR email required" rule. Sending "" for email tripped Pydantic's
			// EmailStr validation, so we omit blank fields and let the backend defaults apply.
			const payload: ContactPayload = {
				captchaSolution: resolveCaptchaSolution(captchaSolution)
			};
			const trimmedEmail = email.trim();
			if (trimmedEmail) payload.email = trimmedEmail;
			const trimmedPhone = phone.trim();
			if (trimmedPhone) payload.phone = trimmedPhone;
			const trimmedName = name.trim();
			if (trimmedName) payload.name = trimmedName;
			if (message?.trim()) {
				payload.message = message.trim();
			}
			const response = await fetch(`${API_URL}/contact-me`, {
				method: 'POST',
				headers: api.formatHeaders({ id }),
				body: JSON.stringify(payload)
			});
			await api.handleResponse(response);
		}
	},
	/**
	 * Token management API endpoints
	 */
	token: {
		/**
		 * Refreshes access token for a policy upload form
		 * @param id - The policy upload form ID
		 * @returns Promise resolving to token refresh response
		 */
		refresh: async (id: string): Promise<TokenRefreshResponse> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}`, {
				method: 'GET',
				headers: api.formatHeaders({ id })
			});
			return api.handleResponse(response) as Promise<TokenRefreshResponse>;
		}
	},
	/**
	 * Share API endpoints
	 */
	share: {
		send: async (
			id: string,
			emails: string[],
			message?: string
		): Promise<{ message: string; skipped_emails: string[] }> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/share`, {
				method: 'POST',
				headers: api.formatHeaders({ id }),
				body: JSON.stringify({ emails, message: message || null })
			});
			if (response.status === 429) {
				throw new RateLimitError();
			}
			return api.handleResponse(response) as Promise<{ message: string; skipped_emails: string[] }>;
		}
	},
	/**
	 * Resume link API endpoints
	 */
	resumeLink: {
		verifyEmail: async (id: string, email: string, expiredToken: string): Promise<unknown> => {
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/verify-email-for-resume`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${expiredToken}`,
					Origin: window.location.origin
				},
				body: JSON.stringify({ email })
			});
			if (response.status === 429) {
				throw new RateLimitError();
			}
			return api.handleResponse(response);
		}
	},
	/**
	 * Activation-related API endpoints
	 */
	activation: {
		/**
		 * Fetches activation redirect URL for application
		 * @param id - The policy upload form ID
		 * @returns Promise resolving to activation response
		 */
		fetch: async (id: string): Promise<ActivationResponse> => {
			const response = await fetch(
				`${API_URL}/policy-upload-forms/${id}/application/activation-redirect-url`,
				{
					method: 'GET',
					headers: api.formatHeaders({ id })
				}
			);
			return api.handleResponse(response) as Promise<ActivationResponse>;
		}
	},
	/**
	 * Employee-related API endpoints
	 */
	employees: {
		/**
		 * Imports employee data from file
		 * @param id - The policy upload form ID
		 * @param file - The employee data file
		 * @returns Promise resolving to employee import response
		 */
		import: async (id: string, file: File): Promise<EmployeeImportResponse> => {
			const formData = api.formatFormData({ file });
			const response = await fetch(`${API_URL}/policy-upload-forms/${id}/employees/import`, {
				method: 'POST',
				headers: api.formatHeaders({ id, contentType: false }),
				body: formData
			});
			return api.handleResponse(response) as Promise<EmployeeImportResponse>;
		}
	},
	/**
	 * File import API endpoints
	 */
	import: {
		/**
		 * Uploads file for employee list extraction
		 * @param id - The policy upload form ID
		 * @param file - The file to upload
		 * @param captchaSolution - The captcha solution
		 * @returns Promise resolving to app data
		 */
		upload: async (id: string, file: File, captchaSolution: string): Promise<AppData> => {
			const data: FormDataFields = {
				file,
				captchaSolution: resolveCaptchaSolution(captchaSolution)
			};
			const formData = api.formatFormData(data);
			const response = await fetch(
				`${API_URL}/policy-upload-forms/${id}/extract-employee-list-file`,
				{
					method: 'POST',
					headers: api.formatHeaders({ id, contentType: false }),
					body: formData
				}
			);
			return api.handleResponse(response) as Promise<AppData>;
		},
		/**
		 * Fetches employee list extraction status. The endpoint returns the
		 * extraction status with the result nested under `result` (see
		 * `ExtractionResponse`), matching what the extraction-upload hook consumes.
		 * @param id - The policy upload form ID
		 */
		fetch: async (id: string): Promise<ExtractionResponse> => {
			const response = await fetch(
				`${API_URL}/policy-upload-forms/${id}/employee-list-extraction-status`,
				{
					method: 'GET',
					headers: api.formatHeaders({ id })
				}
			);
			return api.handleResponse(response) as Promise<ExtractionResponse>;
		}
	},
	/**
	 * Formats HTTP headers for API requests
	 * @param options - Header options including id, contentType, and accept
	 * @returns Object containing formatted headers
	 */
	formatHeaders: ({
		id = null,
		contentType = 'application/json',
		accept = 'application/json'
	}: HeaderOptions = {}): Record<string, string> => {
		const headers: Record<string, string> = {};
		const token = id ? localStorage.getItem(id) : getAvailableBearerToken();
		if (token) headers.Authorization = `Bearer ${token}`;
		if (accept) headers.accept = accept;
		if (contentType) headers['Content-Type'] = contentType;

		// Add label password auth token if available
		// Check for stored token based on current hostname
		const hostname = window.location.hostname;
		const labelSlug = hostname.split('.')[0]; // Extract label from subdomain
		const authToken = localStorage.getItem(`quotr_label_auth_${labelSlug}`);
		if (authToken) {
			headers['X-Label-Password-Auth'] = authToken;
		}

		return headers;
	},
	/**
	 * Formats form data from fields object
	 * @param fields - Object containing form fields
	 * @returns FormData object
	 */
	formatFormData: (fields: FormDataFields): FormData => {
		const formData = new FormData();
		Object.entries(fields).forEach(([key, value]) => {
			formData.append(key, value);
		});
		return formData;
	},
	/**
	 * Handles API response parsing and error checking
	 * @param response - The fetch response object
	 * @returns Promise resolving to parsed response data or null for empty responses
	 * @throws Error if response is not ok or JSON parsing fails
	 */
	handleResponse: async (response: Response): Promise<unknown> => {
		const responseText = await response.text();
		if (!response.ok) {
			let code: string | undefined;
			let message = responseText;
			try {
				const parsed = JSON.parse(responseText);
				if (parsed.detail) {
					if (typeof parsed.detail === 'object' && parsed.detail.code) {
						code = parsed.detail.code;
						message = parsed.detail.message || responseText;
					} else if (typeof parsed.detail === 'string') {
						message = parsed.detail;
					}
				}
			} catch {
				// Not JSON, use raw responseText as message
			}

			if (response.status === 401 && handleExpiredLabelAuth(code, responseText)) {
				throw new ApiError(401, code, 'Authentication expired. Please log in again.');
			}

			throw new ApiError(response.status, code, message);
		}
		if (!responseText || responseText.trim() === '') {
			return null;
		}
		try {
			return JSON.parse(responseText);
		} catch (error) {
			throw new Error('Failed to parse response as JSON', { cause: error });
		}
	}
};

export default api;
